import { useState, useEffect, useCallback } from "react";
import { 
  Wifi, Server, Router, Monitor, AlertTriangle, CheckCircle, XCircle, 
  RefreshCw, Users, Settings, Download, Clock, Signal, Activity,
  ArrowUp, ArrowDown, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

export default function NetworkPage() {
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [devices, setDevices] = useState([]);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState({ alarms: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState({ host: "", username: "", password: "", apiToken: "" });
  const [configuring, setConfiguring] = useState(false);
  const [authMethod, setAuthMethod] = useState("token"); // "token" or "password"

  const fetchData = useCallback(async () => {
    try {
      const statusRes = await api.getUniFiStatus();
      setStatus(statusRes.data);

      if (statusRes.data.connected) {
        const [healthRes, devicesRes, clientsRes, alertsRes] = await Promise.all([
          api.getUniFiHealth(),
          api.getUniFiDevices(),
          api.getUniFiClients(),
          api.getUniFiAlerts(100)
        ]);
        setHealth(healthRes.data);
        setDevices(devicesRes.data.devices || []);
        setClients(clientsRes.data.clients || []);
        setAlerts(alertsRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch UniFi data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleConfigure = async (e) => {
    e.preventDefault();
    setConfiguring(true);
    try {
      if (authMethod === "token") {
        await api.configureUniFi(configForm.host, null, null, configForm.apiToken);
      } else {
        await api.configureUniFi(configForm.host, configForm.username, configForm.password, null);
      }
      toast.success("UniFi configured successfully!");
      setConfigOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to configure UniFi");
    } finally {
      setConfiguring(false);
    }
  };

  const handlePoll = async () => {
    try {
      await api.pollUniFi();
      toast.success("Alert poll completed");
      fetchData();
    } catch (err) {
      toast.error("Failed to poll alerts");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const isConfigured = status?.configured;
  const isConnected = status?.connected;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Wifi className="h-7 w-7 text-blue-500" />
            Network Monitoring
          </h1>
          <p className="text-slate-500 mt-1">UniFi Network Controller</p>
        </div>
        <div className="flex items-center gap-3">
          {isConnected && (
            <Button variant="outline" size="sm" onClick={handlePoll}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Poll Alerts
            </Button>
          )}
          <Dialog open={configOpen} onOpenChange={setConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Configure UniFi Connection</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleConfigure} className="space-y-4 mt-4">
                <div>
                  <Label>Host URL</Label>
                  <Input 
                    placeholder="https://unifi.ui.com or https://192.168.1.1"
                    value={configForm.host}
                    onChange={(e) => setConfigForm({...configForm, host: e.target.value})}
                    required
                  />
                </div>
                
                {/* Auth method toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      authMethod === "token" ? "bg-white shadow text-slate-900" : "text-slate-600"
                    }`}
                    onClick={() => setAuthMethod("token")}
                  >
                    API Token (Recommended)
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      authMethod === "password" ? "bg-white shadow text-slate-900" : "text-slate-600"
                    }`}
                    onClick={() => setAuthMethod("password")}
                  >
                    Username/Password
                  </button>
                </div>
                
                {authMethod === "token" ? (
                  <div>
                    <Label>API Token</Label>
                    <Input 
                      placeholder="wNtKTNeyFYUfH7-DRi3l95Mt2QqB8y_J"
                      value={configForm.apiToken}
                      onChange={(e) => setConfigForm({...configForm, apiToken: e.target.value})}
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Create at: UniFi Console → Settings → Control Plane → API
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Username</Label>
                      <Input 
                        placeholder="Local admin username"
                        value={configForm.username}
                        onChange={(e) => setConfigForm({...configForm, username: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input 
                        type="password"
                        value={configForm.password}
                        onChange={(e) => setConfigForm({...configForm, password: e.target.value})}
                        required
                      />
                    </div>
                  </>
                )}
                
                <Button type="submit" className="w-full" disabled={configuring}>
                  {configuring ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Connect
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!isConfigured ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Wifi className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Connect Your UniFi Network</h3>
            <p className="text-slate-500 mb-4">Enter your UDM-Pro credentials to enable network monitoring</p>
            <Button onClick={() => setConfigOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure Connection
            </Button>
          </CardContent>
        </Card>
      ) : !isConnected ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-amber-800 mb-2">Connection Lost</h3>
            <p className="text-amber-600 mb-4">Unable to reach UniFi controller at {status?.host}</p>
            <Button variant="outline" onClick={() => setConfigOpen(true)}>
              Update Configuration
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Health Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <HealthCard
              title="Gateway"
              icon={<Router className="h-5 w-5" />}
              online={health?.devices?.gateways?.online || 0}
              total={health?.devices?.gateways?.total || 0}
              color="blue"
            />
            <HealthCard
              title="Switches"
              icon={<Server className="h-5 w-5" />}
              online={health?.devices?.switches?.online || 0}
              total={health?.devices?.switches?.total || 0}
              color="purple"
            />
            <HealthCard
              title="Access Points"
              icon={<Wifi className="h-5 w-5" />}
              online={health?.devices?.access_points?.online || 0}
              total={health?.devices?.access_points?.total || 0}
              color="green"
            />
            <HealthCard
              title="Clients"
              icon={<Users className="h-5 w-5" />}
              value={health?.clients?.total || 0}
              subtitle={`${health?.clients?.wireless || 0} wireless / ${health?.clients?.wired || 0} wired`}
              color="amber"
            />
            <HealthCard
              title="Firmware"
              icon={<Download className="h-5 w-5" />}
              value={health?.firmware_updates || 0}
              subtitle="updates available"
              color={health?.firmware_updates > 0 ? "red" : "slate"}
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="devices" className="space-y-4">
            <TabsList>
              <TabsTrigger value="devices">Devices ({devices.length})</TabsTrigger>
              <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
              <TabsTrigger value="alerts">Alerts ({alerts.alarms?.length || 0})</TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
            </TabsList>

            <TabsContent value="devices">
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Device</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Type</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">IP</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Clients</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Uptime</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Version</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {devices.map((device) => (
                        <tr key={device.mac} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <DeviceIcon type={device.type} />
                              <div>
                                <div className="font-medium text-slate-800">{device.name}</div>
                                <div className="text-xs text-slate-400">{device.mac}</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{device.type_label}</td>
                          <td className="p-4 text-sm text-slate-600 font-mono">{device.ip || "-"}</td>
                          <td className="p-4">
                            <StatusBadge status={device.state} upgradable={device.upgradable} />
                          </td>
                          <td className="p-4 text-sm text-slate-600">{device.clients || "-"}</td>
                          <td className="p-4 text-sm text-slate-600">{device.uptime_formatted}</td>
                          <td className="p-4">
                            <div className="text-sm text-slate-600">{device.version}</div>
                            {device.upgradable && (
                              <div className="text-xs text-amber-600">{device.upgrade_to} available</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="clients">
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Client</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">IP</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Connection</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Signal</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {clients.slice(0, 50).map((client) => (
                        <tr key={client.mac} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {client.is_wired ? (
                                <Monitor className="h-5 w-5 text-slate-400" />
                              ) : (
                                <Wifi className="h-5 w-5 text-blue-400" />
                              )}
                              <div>
                                <div className="font-medium text-slate-800">{client.hostname}</div>
                                <div className="text-xs text-slate-400">{client.mac}</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600 font-mono">{client.ip || "-"}</td>
                          <td className="p-4 text-sm text-slate-600">
                            {client.is_wired ? "Wired" : "Wireless"}
                          </td>
                          <td className="p-4">
                            {!client.is_wired && client.signal && (
                              <div className="flex items-center gap-2">
                                <Signal className="h-4 w-4 text-slate-400" />
                                <span className="text-sm">{client.signal} dBm</span>
                              </div>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3 text-xs">
                              <span className="flex items-center gap-1 text-green-600">
                                <ArrowDown className="h-3 w-3" />
                                {formatBytes(client.rx_bytes)}
                              </span>
                              <span className="flex items-center gap-1 text-blue-600">
                                <ArrowUp className="h-3 w-3" />
                                {formatBytes(client.tx_bytes)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="alerts">
              <Card>
                <CardContent className="p-4">
                  {alerts.alarms?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-2" />
                      No active alerts
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {alerts.alarms?.map((alarm, i) => (
                        <div key={alarm.id || i} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                          <div>
                            <div className="font-medium text-red-800">{alarm.message}</div>
                            <div className="text-xs text-red-600 mt-1">{alarm.time}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="events">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {alerts.events?.slice(0, 50).map((event, i) => (
                      <div key={event.id || i} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                        <Activity className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-700 truncate">{event.message}</div>
                          <div className="text-xs text-slate-400">{event.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function HealthCard({ title, icon, online, total, value, subtitle, color }) {
  const isHealthy = total ? online === total : true;
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    slate: "bg-slate-50 text-slate-600"
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
          <span className="text-sm font-medium text-slate-600">{title}</span>
        </div>
        {total !== undefined ? (
          <>
            <div className="text-2xl font-bold text-slate-800">
              {online}<span className="text-slate-400 font-normal">/{total}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {isHealthy ? (
                <span className="text-green-600">All online</span>
              ) : (
                <span className="text-red-600">{total - online} offline</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="text-2xl font-bold text-slate-800">{value}</div>
            <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DeviceIcon({ type }) {
  const icons = {
    uap: <Wifi className="h-5 w-5 text-green-500" />,
    usw: <Server className="h-5 w-5 text-purple-500" />,
    ugw: <Router className="h-5 w-5 text-blue-500" />,
    udm: <Router className="h-5 w-5 text-blue-500" />
  };
  return icons[type] || <Monitor className="h-5 w-5 text-slate-400" />;
}

function StatusBadge({ status, upgradable }) {
  if (status === "online") {
    return (
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
          <CheckCircle className="h-3 w-3" />
          Online
        </span>
        {upgradable && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
            Update
          </span>
        )}
      </div>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
      <XCircle className="h-3 w-3" />
      Offline
    </span>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}
